<template>
  <ion-page>
    <main-header :back-href="locationListHref"></main-header>
    <ion-content padding>
      <form @submit.prevent="handleSubmit()" data-cy="add-form">
        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md>
              <ion-list>
                <input-box
                  :validator="$v.name"
                  data-cy="name"
                  v-model="name"
                  :required="true"
                  :errorMessages="{
                    required: 'Please enter a name.',
                    maxLength: 'Please limit this to 255 characters.'
                  }"
                  maxlength="255"
                >
                  Name
                </input-box>
                <input-box
                  :validator="$v.address"
                  data-cy="address"
                  v-model="address"
                  :required="true"
                  :errorMessages="{
                    required: 'Please enter an address.',
                    maxLength: 'Please limit this to 255 characters.'
                  }"
                  id="address"
                  maxlength="255"
                >
                  Address
                </input-box>
                <input-box
                  :validator="$v.city"
                  data-cy="city"
                  v-model="city"
                  :required="true"
                  :errorMessages="{
                    required: 'Please enter a city.',
                    maxLength: 'Please limit this to 255 characters.'
                  }"
                  maxlength="255"
                >
                  City
                </input-box>
                <ion-item>
                  <ion-label
                    position="stacked"
                    :color="$v.state.$error ? 'danger' : ''"
                  >
                    State
                  </ion-label>
                  <ion-select @ionChange="selectState" :value="state">
                    <ion-select-option
                      v-for="state in states"
                      v-bind:key="`state-${state}`"
                      :value="state"
                      >{{ state }}</ion-select-option
                    >
                  </ion-select>
                </ion-item>
                <ion-item
                  v-if="$v.state.$error && !$v.state.required"
                  color="danger"
                >
                  Please enter a state.
                </ion-item>
                <input-box
                  :validator="$v.zipcode"
                  data-cy="zipcode"
                  v-model="zipcode"
                  :required="true"
                  :errorMessages="{
                    required: 'Please enter a zip code.',
                    validZipcode: 'Please enter a valid zip code.'
                  }"
                  inputmode="numeric"
                >
                  Zipcode
                </input-box>
                <input-box
                  :validator="$v.latitude"
                  data-cy="latitude"
                  v-model="latitude"
                  :required="true"
                  :errorMessages="{
                    required: 'Please enter a latitude.',
                    validLatitude: 'Please enter a valid latitude.'
                  }"
                  inputmode="numeric"
                >
                  Latitude
                </input-box>
                <input-box
                  :validator="$v.longitude"
                  data-cy="longitude"
                  v-model="longitude"
                  :required="true"
                  :errorMessages="{
                    required: 'Please enter a longitude.',
                    validLongitude: 'Please enter a valid longitude.'
                  }"
                  inputmode="numeric"
                >
                  Longitude
                </input-box>
              </ion-list>
            </ion-col>
            <ion-col size="12" size-md>
              <ion-list>
                <ion-item class="toggle-input-box">
                  <ion-label>Accepting Orders</ion-label>
                  <ion-toggle
                    color="success"
                    @ionChange="acceptingOrders = $event.detail.checked"
                    :checked="acceptingOrders"
                  ></ion-toggle>
                </ion-item>

                <ion-item class="toggle-input-box">
                  <ion-label>Delivery</ion-label>
                  <ion-toggle
                    color="success"
                    @ionChange="fulfillmentDelivery = $event.detail.checked"
                    :checked="fulfillmentDelivery"
                  ></ion-toggle>
                </ion-item>

                <ion-item class="toggle-input-box">
                  <ion-label>Pick-up</ion-label>
                  <ion-toggle
                    color="success"
                    @ionChange="fulfillmentPickup = $event.detail.checked"
                    :checked="fulfillmentPickup"
                  ></ion-toggle>
                </ion-item>
                <ion-item v-if="$v.fulfillmentPickup.$error" color="danger">
                  Please enable Delivery, Pick-up, or both.
                </ion-item>

                <input-box
                  v-if="fulfillmentPickup"
                  :validator="$v.pickupInstructions"
                  data-cy="pickupInstructions"
                  v-model="pickupInstructions"
                  :required="fulfillmentPickup"
                  :errorMessages="{
                    required:
                      'Please enter pickup instructions that indicate how the attendee can find their order.',
                    maxLength: 'Please limit this to 1000 characters.'
                  }"
                  maxlength="1000"
                >
                  Pick-up Directions
                </input-box>

                <input-box
                  :validator="$v.serviceCharge"
                  data-cy="service-charge"
                  v-model="serviceCharge"
                  id="service-charge"
                  :required="true"
                  :errorMessages="{
                    required: 'Please enter a service charge.',
                    dollarsAndCents: 'Please enter a valid dollars and cents.'
                  }"
                  inputmode="numeric"
                >
                  Service Charge
                </input-box>
                <ion-item>
                  <ion-label
                    position="stacked"
                    :color="$v.newPhoto.$error ? 'danger' : ''"
                  >
                    Photo
                  </ion-label>
                  <ion-input
                    ref="photo"
                    type="file"
                    accept="image/jpeg, image/png"
                    @change="handlePhotoChanged"
                  />
                </ion-item>
                <ion-item
                  v-if="$v.newPhoto.$error && !$v.newPhoto.required"
                  color="danger"
                >
                  Please choose a photo.
                </ion-item>
                <ion-item
                  v-if="$v.newPhoto.$error && !$v.newPhoto.imageUploadSizeMax"
                  color="danger"
                >
                  The maximum file upload size is limited to 1MB.
                </ion-item>
                <ion-item
                  v-if="
                    $v.newPhoto.$error && !$v.newPhoto.imageDimensionsAreSquare
                  "
                  color="danger"
                >
                  The image must be exactly square.
                </ion-item>
              </ion-list>
            </ion-col>
          </ion-row>
        </ion-grid>
        <div class="ion-padding">
          <submit-button :submitting="submitting" data-cy="add-submit">
            Save and Continue to Menu
          </submit-button>
        </div>
      </form>
    </ion-content>
  </ion-page>
</template>

<script>
import { required, maxLength, requiredIf } from "vuelidate/lib/validators";
import errorAlert from "@/ErrorAlert";
import {
  imageUploadSizeMax,
  imageDimensionsAreSquare,
  validZipcode,
  validLatitude,
  validLongitude,
  validDollarsAndCents
} from "@/validators/validators";
import googlePlacesInit from "@/services/GooglePlaces";
import ImageMixin from "@/mixins/image";
import submittable from "@/mixins/submittable";
import { dollarsToCents } from "@/util";
import { STATE_ABBREVIATIONS } from "@/constants/states";
import { mapGetters } from "vuex";

const deliveryOrPickupRequired = (v, vm) =>
  vm.fulfillmentDelivery || vm.fulfillmentPickup;

let autocomplete;

export default {
  mixins: [ImageMixin, submittable],

  title: "Add Location",

  data: function() {
    return {
      name: "",
      address: "",
      city: "",
      state: "",
      zipcode: "",
      latitude: "",
      longitude: "",
      newPhoto: "",
      serviceCharge: "2.00",
      acceptingOrders: true,
      fulfillmentDelivery: true,
      fulfillmentPickup: false,
      pickupInstructions: "",
      states: STATE_ABBREVIATIONS
    };
  },

  computed: {
    ...mapGetters("auth", ["userIsAdmin"]),

    locationListHref() {
      return this.$router.resolve({
        name: "Web: Location List"
      }).href;
    }
  },

  created() {
    if (!this.userIsAdmin) {
      return this.$router.push("/").catch(e => e);
    }
  },

  async mounted() {
    try {
      const google = await googlePlacesInit();

      // have to give time for google to remount when we re-visit this
      setTimeout(() => {
        autocomplete = new google.maps.places.Autocomplete(
          document.querySelector("#address input"),
          {
            types: ["geocode"]
          }
        );
        autocomplete.setFields(["address_component", "geometry"]);
        autocomplete.addListener("place_changed", this.fillInAddress);
      }, 500);
    } catch (error) {
      errorAlert(error.message);
    }
  },

  methods: {
    fillInAddress() {
      let place = autocomplete.getPlace();

      try {
        let parts = Object.fromEntries(
          new Map(
            place.address_components.map(component => [
              component.types[0],
              {
                short_name: component.short_name,
                long_name: component.long_name
              }
            ])
          ).entries()
        );

        parts.latitude = place.geometry.location.lat();
        parts.longitude = place.geometry.location.lng();

        this.address = `${parts.street_number.long_name} ${parts.route.long_name}`;
        this.city = parts.locality.long_name;
        this.state = parts.administrative_area_level_1.short_name;
        this.zipcode = parts.postal_code.short_name;
        this.latitude = parts.latitude;
        this.longitude = parts.longitude;
      } catch (e) {
        errorAlert("Unable to convert this address. Please enter it by hand.");
      }
    },

    selectState(event) {
      this.state = event.detail.value;
    },

    async handleSubmit() {
      this.$v.$touch();

      // right now touch is not promisified - I couldn't get it to work well anyway
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!this.$v.$invalid) {
        let newLocationRef = this.$firestore()
          .collection("locations")
          .doc();

        let updateParams = {
          name: this.name,
          address: this.address,
          city: this.city,
          state: this.state,
          zipcode: this.zipcode,
          geoPoint: new this.$firestore.GeoPoint(
            parseFloat(this.latitude),
            parseFloat(this.longitude)
          ),
          serviceCharge: dollarsToCents(this.serviceCharge),
          acceptingOrders: this.acceptingOrders,
          fulfillmentDelivery: this.fulfillmentDelivery,
          fulfillmentPickup: this.fulfillmentPickup,
          pickupInstructions: this.pickupInstructions,
          created: this.$firestore.FieldValue.serverTimestamp(),
          updated: this.$firestore.FieldValue.serverTimestamp()
        };

        let ref = this.$storage().ref();
        let uploadRef = ref.child(
          this.generateLocationImagePath(newLocationRef)
        );

        const uploadTaskSnapshot = await uploadRef.put(this.newPhoto, {
          contentType: this.newPhoto.type
        });

        const downloadURL = await uploadTaskSnapshot.ref.getDownloadURL();
        updateParams.photoURL = downloadURL;

        newLocationRef
          .set(updateParams)
          .then(() => {
            const menuParams = {
              created: this.$firestore.FieldValue.serverTimestamp(),
              updated: this.$firestore.FieldValue.serverTimestamp()
            };

            return this.$firestore()
              .collection("menus")
              .doc(newLocationRef.id)
              .set(menuParams);
          })
          .then(() => {
            this.successToast("The location has been added.");
            this.$router.push({
              name: "Web: Location Menu Edit",
              params: {
                id: newLocationRef.id
              }
            });
          })
          .catch(e => {
            errorAlert(e.message);
          });
      }
    },

    handlePhotoChanged(event) {
      let files = event.target.files || event.dataTransfer.files;
      if (!files.length) {
        return;
      }
      this.newPhoto = files[0];
      this.$v.newPhoto.$reset();
    }
  },

  validations: {
    name: {
      required,
      maxLength: maxLength(255)
      // I'm not checking for duplicate name because I guess it's technically possible there would be similar/same names, different cities
    },

    address: {
      required,
      maxLength: maxLength(255)
    },

    city: {
      required,
      maxLength: maxLength(255)
    },

    state: {
      required
    },

    zipcode: {
      required,
      validZipcode
    },

    latitude: {
      required,
      validLatitude
    },

    longitude: {
      required,
      validLongitude
    },

    fulfillmentPickup: {
      deliveryOrPickupRequired
    },

    pickupInstructions: {
      required: requiredIf("fulfillmentPickup"),
      maxLength: maxLength(1000)
    },

    /**
     * Remember, this only works because we're doing web-only
     * In the future if this is for mobile, we'd need to get that working with a plugin
     * Also we're going to rely on the browser for validation - because these are admin
     */
    newPhoto: {
      required,
      imageUploadSizeMax,
      imageDimensionsAreSquare
    },

    serviceCharge: {
      required,
      validDollarsAndCents
    }

    // not sure if boolean can be validated properly here
  }
};
</script>

<style lang="scss">
.toggle-input-box {
  ion-label {
    padding-top: 15px;
  }
}
</style>
